import { Feature } from "geojson";
import { useCallback, useRef, useState } from "react";
import { CanvasSourceSpecification, Layer, Source } from "react-map-gl/maplibre";

import { useAnimationFrame } from "@nens/react/hooks";

import { useConfigHooks } from "./config-hooks";
import { usePrepareHooks } from "./prepare-hooks";
import { useRedrawCanvasHooks } from "./redraw-canvas-hooks";
import { Bounds, TerrainCanvasInfo, TwoDFlowData, TwoDWaterflowConfig, WindowWind } from "./types";

/* tslint:disable */
/* eslint-disable */
type CanvasSourceAny = any;

// Canvas needs to be rendered otherwise traces leave a trail behind
const canvasStyle: React.CSSProperties = {
  position: "absolute",
  zIndex: "99999999999",
  pointerEvents: "none",
  height: "5px",
  width: "5px",
};

export type TwoDWaterflowProps = {
  features: Feature[];
  twoDFlowData: TwoDFlowData;
  config?: TwoDWaterflowConfig;
  terrainCanvasInfo?: TerrainCanvasInfo | null;
};

// This is the collection of all the logic of the 2D flow into canvasses
export const TwoDWaterflow: React.FC<TwoDWaterflowProps> = (props) => {
  const [activeCanvasFlipped, setActiveCanvasFlipped] = useState(false);
  const [hideCanvasses, setHideCanvasses] = useState(false);
  const [animateBoth, setAnimateBoth] = useState(false);
  const [savedBounds, setSavedBounds] = useState<Bounds | null>(null);

  const canvasRef1 = useRef<HTMLCanvasElement>(null);
  const canvasRef2 = useRef<HTMLCanvasElement>(null);

  const getCanvasses = useCallback(
    () => ({
      canvas1: activeCanvasFlipped ? canvasRef1.current : canvasRef2.current,
      canvas2: !activeCanvasFlipped ? canvasRef1.current : canvasRef2.current,
    }),
    [activeCanvasFlipped]
  );

  const { windData, basicInfo } = usePrepareHooks({
    ...props,
    getCanvasses,
  });

  const { redrawCanvas } = useRedrawCanvasHooks({
    ...props,
    getCanvasses,
    basicInfo,
    windData,
    hideCanvasses,
    activeCanvasFlipped,
    setActiveCanvasFlipped,
    savedBounds,
    setSavedBounds,
    setAnimateBoth,
  });

  useConfigHooks({
    ...props,
    setHideCanvasses,
    redrawCanvas,
    basicInfo,
  });

  useAnimationFrame(() => {
    if (hideCanvasses) return;
    const windWindow = window as WindowWind;

    const activeWind = activeCanvasFlipped ? windWindow.wind1 : windWindow.wind2;
    if (activeWind && activeWind.windData) activeWind.draw();

    if (animateBoth) {
      const nonActiveWind = !activeCanvasFlipped ? windWindow.wind1 : windWindow.wind2;
      if (nonActiveWind && nonActiveWind.windData) nonActiveWind.draw();
    }
  }, 60);

  const sourceCoordinates = savedBounds
    ? ([
        [savedBounds[0], savedBounds[1]],
        [savedBounds[2], savedBounds[1]],
        [savedBounds[2], savedBounds[3]],
        [savedBounds[0], savedBounds[3]],
      ] as [[number, number], [number, number], [number, number], [number, number]])
    : null;

  return (
    <>
      <canvas id="canvas1" ref={canvasRef1} style={canvasStyle} />
      <canvas id="canvas2" ref={canvasRef2} style={canvasStyle} />

      {sourceCoordinates && !hideCanvasses && (
        <>
          <Source
            {...({
              type: "canvas",
              canvas: "canvas1",
              id: "canvas-source",
              coordinates: sourceCoordinates,
            } as CanvasSourceSpecification as CanvasSourceAny)}
          >
            <Layer
              id="canvas-layer-1"
              type="raster"
              source="canvas-source"
              paint={{
                "raster-resampling": "linear",
              }}
              layout={{
                visibility: !activeCanvasFlipped ? "visible" : "none",
              }}
            />
          </Source>

          <Source
            {...({
              type: "canvas",
              canvas: "canvas2",
              id: "canvas-source-2",
              coordinates: sourceCoordinates,
            } as CanvasSourceSpecification as CanvasSourceAny)}
          >
            <Layer
              id="canvas-layer-2"
              type="raster"
              source="canvas-source-2"
              paint={{
                "raster-resampling": "linear",
              }}
              layout={{
                visibility: activeCanvasFlipped ? "visible" : "none",
              }}
            />
          </Source>
        </>
      )}

      {/* USED FOR TESTING */}
      {/* <canvas
        id="testing"
        style={{
          position: "absolute",
          pointerEvents: "none",
          zIndex: "2000",
          left: "0",
          top: "0",
          width: "1px",
          height: "1px",
        }}
      /> */}
    </>
  );
};
